<template>
  <div class="cec-input-data position-relative">
    <!-- 1. Input de Texto visível (Formatado BR ou Comandos) -->
    <b-form-input
      v-model="textInput"
      type="text"
      :placeholder="placeholderText"
      @keydown.enter="processInput"
      @keydown.up.prevent="adjustDate(1)"
      @keydown.down.prevent="adjustDate(-1)"
      @blur="processInput"
      ref="textInputRef"
      :state="state"
      :disabled="disabled"
      :class="['form-control-icon-padding', inputClass]"
      style="width: 100% !important;"
      autocomplete="off"
    />

    <!-- 2. Botão Limpar (X) -->
    <!-- Exibir apenas se houver valor válido (internalISODate) e não estiver desabilitado -->
    <span
      v-show="!disabled && hasValidDate"
      class="cec-input-icon cec-clear-icon fa fa-times"
      @mousedown.prevent="onClear"
      title="Limpar campo"
    >
      
    </span>

    <!-- 3. Área do Calendário -->
    <!-- Container para o ícone e o input date invisível sobreposto -->
    <div class="cec-calendar-wrapper" v-if="!disabled">
      <!-- Ícone Visual -->
      <span class="cec-input-icon cec-calendar-icon fa fa-calendar-o" title="Abrir calendário">
        
      </span>
      
      <!-- Input Date Invisível (Overlay) -->
      <!-- Ao clicar nele, o browser abre o calendário nativo. 
           Sincronizamos o valor dele com internalISODate -->
      <b-form-input
        v-model="internalISODate"
        type="date"
        class="cec-date-overlay"
        tabindex="-1"
        @input="onDatePick"
      ></b-form-input>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

export default {
  name: 'CecInputData',
  props: {
    value: {
      type: String,
      default: '',
    },
    state: {
      type: Boolean,
      default: null, // null = sem validação visual, true = verde, false = vermelho
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholderText: {
      type: String,
      default: '',
    },
    inputClass: { type: [String, Object, Array], default: '' }
  },
  data() {
    return {
      textInput: '',         // O que o usuário vê (DD/MM/YYYY ou texto sujo)
      internalISODate: '',   // Valor real (YYYY-MM-DD)
    };
  },
  computed: {
    hasValidDate() {
      // Verifica se temos uma data ISO válida armazenada
      return this.internalISODate && dayjs(this.internalISODate).isValid();
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal !== this.internalISODate) {
          this.internalISODate = newVal || '';
          this.syncTextFromISO();
        }
      }
    }
  },
  methods: {
    normalizeDateValue(value) {
      if (!value) return '';
      if (typeof value === 'string') {
        const matchIso = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (matchIso) return `${matchIso[1]}-${matchIso[2]}-${matchIso[3]}`;
      }
      const parsed = dayjs(value);
      return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
    },
    focus() {
      if (this.$refs.textInputRef && this.$refs.textInputRef.focus) {
        this.$refs.textInputRef.focus();
      }
    },
    // Atualiza o texto visual baseado no ISO atual
    syncTextFromISO() {
      const iso = this.normalizeDateValue(this.internalISODate);
      if (iso) {
        this.internalISODate = iso;
        const [year, month, day] = iso.split('-');
        this.textInput = `${day}/${month}/${year}`;
      } else {
        // Se a data interna limpou, limpa o texto também, a menos que o usuário esteja digitando algo (controlado pelo blur)
        // Aqui assumimos que se o value mudou externamente para vazio, limpamos tudo.
        if (!this.internalISODate) this.textInput = '';
      }
    },

    // Chamado quando o usuário escolhe uma data no calendário nativo (input date overlay)
    onDatePick(val) {
      // val já vem no formato YYYY-MM-DD do input date
      this.internalISODate = val;
      this.syncTextFromISO();
      this.$emit('input', this.internalISODate);
      
      // Opcional: focar no texto após seleção? Normalmente não precisa.
    },

    // Processa o que foi digitado no campo de texto (Blur ou Enter)
    processInput() {
      if (this.disabled) return;
      
      const raw = this.textInput.trim().toLowerCase();
      if (!raw) {
        // Se limpar o campo e sair, não queremos focar de volta (loop de foco)
        this.onClear(false);
        return;
      }

      // 1. Tentar comandos (h, o, a, i, f)
      const regex = /^([hoafi])([+-]?\d*)$/; 
      const match = raw.match(regex);
      
      let newDate = null;
      if (match) {
        const command = match[1]; 
        const offsetStr = match[2]; 
        const offset = offsetStr ? parseInt(offsetStr, 10) : 0; 

        if (command === 'h') newDate = dayjs().add(offset, 'day');
        else if (command === 'o') newDate = dayjs().subtract(1, 'day').add(offset, 'day');
        else if (command === 'a') newDate = dayjs().add(1, 'day').add(offset, 'day');
        else if (command === 'i') newDate = dayjs().startOf('month').add(offset, 'day');
        else if (command === 'f') newDate = dayjs().endOf('month').add(offset, 'day');
      } 
      // 2. Tentar data formatada BR (DD/MM/YYYY)
      else if (dayjs(raw, 'DD/MM/YYYY', true).isValid()) {
         // strict mode true para evitar falsos positivos
         newDate = dayjs(raw, 'DD/MM/YYYY');
      }

      // Validação final
      if (newDate && newDate.isValid()) {
        const iso = newDate.format('YYYY-MM-DD');
        this.internalISODate = iso;
        this.textInput = newDate.format('DD/MM/YYYY');
        this.$emit('input', iso);
      } else {
        // Entrada inválida
        if (this.hasValidDate) {
          this.textInput = dayjs(this.internalISODate).format('DD/MM/YYYY');
        } else {
          this.textInput = '';
          this.internalISODate = '';
          this.$emit('input', '');
        }
      }
    },

    onClear(refocus = true) {
      if (this.disabled) return;
      this.textInput = '';
      this.internalISODate = '';
      this.$emit('input', '');
      
      if (refocus) {
        // Foca no input texto
        this.$nextTick(() => {
          this.focus();
        });
      }
    },

    adjustDate(days) {
      if (this.disabled || !this.hasValidDate) return;
      const current = dayjs(this.internalISODate);
      if (current.isValid()) {
        const newDate = current.add(days, 'day');
        this.internalISODate = newDate.format('YYYY-MM-DD');
        this.syncTextFromISO();
        this.$emit('input', this.internalISODate);
      }
    },

    focus() {
      if (this.$refs.textInputRef) {
        this.$refs.textInputRef.focus();
      }
    }
  }
};
</script>

<style scoped>
.cec-input-data {
  width: 100%;
}

/* Padding para caber os ícones */
.cec-input-data .form-control-icon-padding {
  padding-right: 3.5rem !important; /* Reduzido de 4.5rem para ser mais compacto */
}

/* Se tiver validação bootstrap (is-invalid/is-valid), o ícone fica na direita (padrão)
   e deslocamos nossos ícones para a esquerda */
.cec-input-data .form-control.is-invalid,
.cec-input-data .form-control.is-valid {
  padding-right: 6.5rem !important; /* Mais espaço para caber tudo */
  background-position: right 0.75rem center !important; /* Ícone de validação na ponta direita */
}

/* Quando tem validação, movemos o Wrapper do Calendário para a esquerda do ícone de validação */
.cec-input-data .form-control.is-valid ~ .cec-calendar-wrapper,
.cec-input-data .form-control.is-invalid ~ .cec-calendar-wrapper {
  right: 2.2rem; 
}

/* Quando tem validação, movemos o Botão Limpar mais para a esquerda ainda */
.cec-input-data .form-control.is-valid ~ .cec-clear-icon,
.cec-input-data .form-control.is-invalid ~ .cec-clear-icon {
  right: 4.2rem;
}

/* Ajuste específico para quando o botão limpar está sozinho (sem calendário) mas COM validação */
.cec-input-data .form-control.is-valid ~ .cec-clear-icon.cec-clear-solo,
.cec-input-data .form-control.is-invalid ~ .cec-clear-icon.cec-clear-solo {
  right: 2.5rem !important;
}

/* Ícone geral */
.cec-input-icon {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  user-select: none;
}

/* Botão Limpar (X) padrão (sem validação) */
.cec-clear-icon {
  right: 1.8rem; /* Reduzido de 2.2rem */
  width: 20px;
  height: 20px;
  cursor: pointer;
  font-size: 0.85rem; /* Reduzido de 0.85rem (emoji) para icon font awesome, ajustando para ficar bom */
  color: #6c757d; 
}

/* Quando o calendário não está visível */
.cec-clear-solo {
  right: 0.5rem !important;
}

/* Wrapper do Calendário padrão (sem validação) */
.cec-calendar-wrapper {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  z-index: 11; 
  display: flex;
  align-items: center;
  justify-content: center;
}

.cec-calendar-icon {
  pointer-events: none; 
  font-size: 0.75rem; /* Reduzido de 1.2rem */
  color: #6c757d; /* Cor cinza padrão */

}

/* O input date invisível que captura o clique */
.cec-date-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 20; 
  padding: 0;
  margin: 0;
  border: none;
}

.cec-input-data .form-control.rounded-pill,
.cec-input-data .form-control.rounded-pill.is-valid,
.cec-input-data .form-control.rounded-pill.is-invalid {
  border-radius: 50rem !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Cec/CecInputData.vue